import { supabase } from '@/core/supabase/client';

export interface OrganizationMembership {
  organization_id: string;
  name: string;
  kind: 'solo' | 'team';
  role: 'admin' | 'member';
  is_admin: boolean;
}

export interface PendingInvitation {
  invitation_id: string;
  organization_id: string;
  organization_name: string;
  role: 'admin' | 'member';
  invited_at: string;
}

/** Organizaciones donde el usuario es miembro ACTIVO. La fuente es RLS, no la UI. */
export async function fetchMyOrganizations(): Promise<OrganizationMembership[]> {
  const { data, error } = await supabase.rpc('my_organizations');
  if (error) throw error;
  return (data ?? []) as OrganizationMembership[];
}

/** Lo que el asistente de arranque fue preguntando. */
export interface AltaDeVeterinaria {
  name: string;
  kind: 'solo' | 'team';
  /** Con este nombre va a figurar en los repartos. No es el de la veterinaria. */
  my_display_name: string;
  charge_mobility: boolean;
  mobility_mode: 'none' | 'per_km_fuel' | 'per_km_fixed' | 'flat' | 'manual';
  mobility_rate_cents?: number | null;
  flat_mobility_cents?: number | null;
  /** El precio del novillo del mes. Sin él la aplicación anda: los importes quedan pendientes. */
  steer_price_cents?: number | null;
  fuel_price_cents?: number | null;
  price_year?: number;
  price_month?: number;
}

/**
 * Da de alta una veterinaria y deja a quien la crea listo para trabajar.
 *
 * Una sola llamada y una sola transacción. Si algo falla no queda nada a medio
 * crear: una organización sin participante ni ajustes deja a su dueño adentro
 * de una aplicación rota y sin salida, que es peor que no haberla creado.
 */
export async function crearOrganizacion(datos: AltaDeVeterinaria): Promise<string> {
  const { data, error } = await supabase.rpc('create_organization', { p: datos });
  if (error) throw error;
  return data as string;
}

export interface OrgUser {
  profile_id: string;
  name: string;
  /**
   * El id de la MEMBRESÍA, no el del perfil.
   *
   * Es lo que guarda `participants.member_id`, y la diferencia importa: el
   * perfil es la persona en todo el sistema, la membresía es esa persona
   * dentro de ESTA veterinaria. Vincular por perfil dejaría que un
   * participante de una organización apuntara a alguien de otra.
   */
  member_id: string;
}

/**
 * Miembros de la organización, para filtrar el listado por "quién lo cargó".
 * Depende de las policies de `organization_members` y `profiles`: lo que RLS no
 * deja ver, acá tampoco aparece.
 */
export async function listOrgUsers(organizationId: string): Promise<OrgUser[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('id, profile_id, profile:profiles(full_name, email)')
    .eq('organization_id', organizationId)
    .neq('status', 'invited');
  if (error) throw error;

  return (data ?? [])
    .map((row) => {
      // PostgREST tipa la relación como arreglo aunque sea 1:1.
      const r = row as unknown as {
        id: string;
        profile_id: string;
        profile: { full_name: string | null; email: string } | null;
      };
      return {
        profile_id: r.profile_id,
        member_id: r.id,
        name: r.profile?.full_name || r.profile?.email || 'Sin nombre',
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export async function fetchMyInvitations(): Promise<PendingInvitation[]> {
  const { data, error } = await supabase.rpc('my_pending_invitations');
  if (error) throw error;
  return (data ?? []) as PendingInvitation[];
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase.rpc('accept_invitation', { p_invitation: invitationId });
  if (error) throw error;
}

/**
 * Qué pasó con la invitación.
 *
 * Son dos cosas distintas y hay que decirlas distinto. Antes esto devolvía
 * `void` y la pantalla anunciaba «Invitación enviada» con sólo haber insertado
 * una fila: no se mandaba ningún correo, y quien invitaba se quedaba esperando
 * una respuesta que nunca iba a llegar.
 */
export type ResultadoInvitacion =
  /** La invitación quedó creada y el correo salió. */
  | { resultado: 'enviada'; email: string }
  /**
   * La invitación quedó creada —y sirve— pero el aviso no salió. Quien invitó
   * tiene que enterarse para avisar por otro medio.
   */
  | { resultado: 'creada_sin_aviso'; motivo: string }
  /**
   * La persona ya tenía cuenta, así que no hay correo que mandar: a quien ya
   * está registrado no se lo puede invitar por mail. La invitación la va a ver
   * en la aplicación apenas entre. No es una falla, y decirlo como falla manda
   * a buscar un problema que no existe.
   */
  | { resultado: 'ya_tenia_cuenta'; email: string }
  /** No se pudo ni crear. `etapa` dice dónde se cortó. */
  | { resultado: 'error'; etapa: string; detalle?: string };

/**
 * El mensaje que importa, adentro del error que devuelve PostgREST.
 *
 * Un error de la base vuelve envuelto en JSON:
 * `{"code":"23514","message":"Tu plan incluye 1 usuario(s)...","hint":null}`.
 * Mostrarlo entero obliga a leer plomería para encontrar la frase; mostrar
 * sólo el texto deja exactamente lo que hay que hacer.
 */
function mensajeDePostgrest(texto: string): string {
  try {
    const o = JSON.parse(texto) as Record<string, unknown>;
    for (const campo of ['message', 'msg', 'details', 'hint', 'error_description', 'error']) {
      const v = o[campo];
      if (typeof v === 'string' && v.trim() !== '') return v;
    }
  } catch {
    // No era JSON: entonces ya es el texto.
  }
  return texto;
}

/**
 * Lo que el servidor dijo que pasó, cuando `functions.invoke` lo tapa.
 *
 * Esto existe por un defecto que escondía TODOS los motivos detrás de la misma
 * frase. `invitar-persona` contesta con cuidado —«no sos administrador», «tu
 * sesión venció», «tu plan incluye 1 usuario y ya están todos ocupados»— y lo
 * manda en el cuerpo con el código HTTP que corresponde. Pero ante cualquier
 * respuesta que no sea 2xx, `supabase-js` descarta el cuerpo y devuelve un
 * error suyo que dice «Edge Function returned a non-2xx status code»: un texto
 * en inglés, sobre la plomería, idéntico para las cinco causas posibles. Quien
 * invitaba no tenía forma de saber si el problema era el correo mal escrito,
 * el permiso, la sesión o el plan.
 *
 * El cuerpo sigue estando: viaja en `context`, que es la respuesta HTTP.
 */
async function motivoDelServidor(error: unknown): Promise<string | null> {
  const respuesta = (error as { context?: { json?: unknown } }).context;
  if (!respuesta || typeof respuesta.json !== 'function') return null;

  let cuerpo: unknown;
  try {
    cuerpo = await (respuesta as Response).json();
  } catch {
    return null;
  }
  if (!cuerpo || typeof cuerpo !== 'object') return null;
  const c = cuerpo as Record<string, unknown>;

  if (typeof c.detalle === 'string' && c.detalle.trim() !== '') {
    return mensajeDePostgrest(c.detalle);
  }

  // Sin detalle queda la etapa, que dice dónde se cortó. Son las dos fallas
  // del servidor mismo: no son culpa de quien invita, y el texto tiene que
  // decirlo para que no se quede probando de nuevo con otro correo.
  const porEtapa: Record<string, string> = {
    configuracion:
      'El servidor de invitaciones está mal configurado. Avisanos: no es algo que puedas resolver desde acá.',
    consulta_membresia:
      'No se pudo verificar que seas administrador de esta veterinaria. Probá de nuevo en un rato.',
  };
  if (typeof c.etapa === 'string') {
    return porEtapa[c.etapa] ?? `No se pudo invitar (${c.etapa}).`;
  }
  return null;
}

export async function inviteUser(input: {
  organizationId: string;
  email: string;
  role: 'admin' | 'member';
  /**
   * Quién va a ser esta persona en el reparto, si ya figura ahí.
   *
   * Se vincula sola al aceptar. Sin esto, alguien que cobra en los repartos
   * desde hace meses entra y lee «No participás económicamente en este
   * período»: RLS le oculta sus propias líneas porque no hay forma de saber
   * que son suyas.
   */
  participantId?: string | null;
  // El caso `error` no aparece acá porque se lanza: quien llama se ocupa de
  // los dos desenlaces que dejan la invitación creada, y de nada más.
}): Promise<Exclude<ResultadoInvitacion, { resultado: 'error' }>> {
  // Del lado del servidor: mandar el correo necesita la llave de servicio, que
  // no puede viajar al teléfono.
  const { data, error } = await supabase.functions.invoke('invitar-persona', {
    body: {
      organization_id: input.organizationId,
      email: input.email,
      role: input.role,
      participant_id: input.participantId ?? null,
      redirect_to: window.location.origin,
    },
  });
  if (error) {
    // `functions.invoke` informa cualquier corte de red con un mismo texto:
    // «Failed to send a request to the Edge Function». Eso mandó a buscar la
    // función caída cuando lo que faltaba era el permiso CORS, y va a volver a
    // pasar con el teléfono sin señal.
    const crudo = (error as Error).message;
    if (/failed to send a request/i.test(crudo)) {
      throw new Error('No se pudo contactar al servidor. Revisá la conexión y probá de nuevo.');
    }
    // El motivo real viaja en el cuerpo de la respuesta. Si no se puede leer
    // queda el texto crudo, que al menos sirve para diagnosticar.
    throw new Error((await motivoDelServidor(error)) ?? crudo);
  }
  const r = data as ResultadoInvitacion;
  // Mientras la versión vieja de la función siga desplegada, el caso «ya tenía
  // cuenta» llega como `creada_sin_aviso` con el texto de Supabase adentro. Se
  // traduce acá para que la pantalla diga lo correcto sin esperar al despliegue.
  if (r.resultado === 'creada_sin_aviso' && /already been registered|email_exists/i.test(r.motivo)) {
    return { resultado: 'ya_tenia_cuenta', email: input.email };
  }
  if (r.resultado === 'error') {
    throw new Error(
      r.detalle ? mensajeDePostgrest(r.detalle) : `No se pudo invitar (${r.etapa}).`,
    );
  }
  return r;
}

/**
 * Decir que un usuario de la veterinaria es tal participante del reparto.
 *
 * Con `memberId` en `null` lo desvincula, que es cómo se corrige haberse
 * equivocado de persona. Desvincular no borra nada: el participante sigue
 * cobrando lo mismo; lo único que deja de hacer es mostrarle sus números a ese
 * usuario.
 *
 * Quién puede hacerlo lo decide la base, no esta función.
 */
export async function linkParticipantToMember(
  participantId: string,
  memberId: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('link_participant_to_member', {
    p_participant: participantId,
    p_member: memberId,
  });
  if (error) throw error;
}
